<!-- eslint-disable max-len -->
<template>
  <b-modal
    v-model="showModal"
    title="Track Spending"
    size="lg"
    centered
    hide-footer
    @hidden="resetForm"
  >
    <div class="spending-tracker">
      <!-- Budget Summary -->
      <div v-if="budget" class="budget-summary">
        <h5 class="cabin-family semi-bold">{{ budgetName }}</h5>
        <div class="summary-stats">
          <div class="stat-item">
            <span class="label secondary-text fnt-10">Total Budget</span>
            <span class="value cabin-family semi-bold">{{ formatCurrency(totalBudget) }}</span>
          </div>
          <div class="stat-item">
            <span class="label secondary-text fnt-10">Spent</span>
            <span class="value cabin-family semi-bold primary">{{ formatCurrency(totalSpent) }}</span>
          </div>
          <div class="stat-item">
            <span class="label secondary-text fnt-10">Remaining</span>
            <span class="value cabin-family semi-bold success">{{ formatCurrency(totalRemaining) }}</span>
          </div>
        </div>
      </div>

      <!-- Spending Form -->
      <form @submit.prevent="submitSpending">
        <div class="form-group">
          <label for="category" class="form-label cabin-family semi-bold">
            Category *
          </label>
          <select
            v-model="form.category"
            class="form-control"
            :class="{ 'is-invalid': !!errors.category }"
          >
            <option value="">Select a category</option>
            <option
              v-for="(categoryData, categoryName) in budgetCategories"
              :key="categoryName"
              :value="categoryName"
            >
              {{ formatCategoryName(categoryName) }}
              ({{ formatCurrency(getCategorySpent(categoryName)) }} / {{ formatCurrency(categoryData.amount || 0) }})
            </option>
          </select>
          <div v-if="errors.category" class="error-message">
            {{ errors.category }}
          </div>
        </div>

        <div class="form-group">
          <label for="amount" class="form-label cabin-family semi-bold">
            Amount *
          </label>
          <CurrencyInput
            v-model="form.amount"
            :has-error="!!errors.amount"
            :error-message="errors.amount"
            placeholder="Enter spending amount"
            @error="handleInputError"
          />
        </div>

        <div class="form-group">
          <label for="description" class="form-label cabin-family semi-bold">
            Description *
          </label>
          <input
            v-model="form.description"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': !!errors.description }"
            placeholder="What did you spend on?"
          />
          <div v-if="errors.description" class="error-message">
            {{ errors.description }}
          </div>
        </div>

        <div class="form-group">
          <label for="spendingDate" class="form-label cabin-family semi-bold">
            Date
          </label>
          <DatePicker
            v-model="form.spendingDate"
            :has-error="!!errors.spendingDate"
            :error-message="errors.spendingDate"
            :max-date="new Date().toISOString().split('T')[0]"
          />
        </div>

        <!-- Category Impact (if category selected) -->
        <div v-if="selectedCategoryImpact" class="category-impact">
          <h6 class="cabin-family semi-bold">Category Impact</h6>
          <div class="impact-card">
            <div class="impact-header">
              <h6>{{ formatCategoryName(form.category) }}</h6>
              <span
                class="impact-status"
                :class="getImpactStatusClass()"
              >
                {{ getImpactStatus() }}
              </span>
            </div>

            <div class="impact-progress">
              <div class="progress-info">
                <span>{{ formatCurrency(selectedCategoryImpact.newSpent) }}</span>
                <span class="secondary-text">of {{ formatCurrency(selectedCategoryImpact.allocated) }}</span>
              </div>
              <ProgressBar
                v-if="selectedCategoryImpact && selectedCategoryImpact.newProgress !== null && selectedCategoryImpact.newProgress !== undefined"
                :percentage="selectedCategoryImpact.newProgress"
                :color="getProgressColor(selectedCategoryImpact.newProgress)"
                height="6px"
              />
            </div>

            <div v-if="selectedCategoryImpact.newProgress > 100" class="over-budget-warning">
              <i class="fas fa-exclamation-triangle"></i>
              This will put you {{ formatCurrency(selectedCategoryImpact.overBudget) }} over budget for this category.
            </div>
          </div>
        </div>

        <!-- Recent Spending -->
        <div v-if="recentSpending.length > 0" class="recent-spending">
          <h6 class="cabin-family semi-bold">Recent Spending</h6>
          <div class="spending-list">
            <div
              v-for="entry in recentSpending"
              :key="entry.id"
              class="spending-entry"
            >
              <div class="entry-info">
                <span class="entry-description">{{ entry.description }}</span>
                <span class="entry-category secondary-text fnt-10">
                  {{ formatCategoryName(entry.category) }}
                </span>
              </div>
              <div class="entry-details">
                <span class="entry-amount primary cabin-family semi-bold">
                  {{ formatCurrency(entry.amount) }}
                </span>
                <span class="entry-date secondary-text fnt-10">
                  {{ formatDate(entry.spending_date) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="closeModal"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn primary-btn"
            :disabled="loading || !isFormValid"
          >
            <LoadingSpinner
              v-if="loading"
              size="small"
              variant="white"
            />
            <span v-else>Track Spending</span>
          </button>
        </div>
      </form>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { formatCurrency, formatDate } from '../../utils/formatters';
import {
  CurrencyInput, DatePicker, LoadingSpinner, ProgressBar,
} from '../shared';

export default {
  name: 'SpendingTracker',
  components: {
    CurrencyInput,
    DatePicker,
    LoadingSpinner,
    ProgressBar,
  },
  props: {
    budget: {
      type: Object,
      default: null,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showModal: false,
      loading: false,
      form: {
        category: '',
        amount: '',
        description: '',
        spendingDate: new Date().toISOString().split('T')[0],
      },
      errors: {},
    };
  },
  computed: {
    ...mapState('budgets', ['spendingEntries']),

    isFormValid() {
      return this.form.category
             && this.form.amount
             && this.form.amount > 0
             && this.form.description
             && !this.errors.amount
             && !this.errors.category
             && !this.errors.description;
    },

    totalBudget() {
      if (!this.budget || !this.budget.categories) return 0;
      return Object.values(this.budget.categories)
        .reduce((total, cat) => total + (cat.amount || 0), 0);
    },

    totalSpent() {
      if (!this.budget) return 0;

      let totalSpent = 0;

      // Calculate from performance data if available
      if (this.budget.performance && this.budget.performance.overall) {
        totalSpent = this.budget.performance.overall.totalActual || 0;
      } else if (this.budget.recent_tracking && this.budget.recent_tracking.length > 0) {
        // Use recent_tracking from new API format
        totalSpent = this.budget.recent_tracking.reduce(
          (total, tracking) => total + (tracking.amount || 0), 0,
        );
      } else if (this.budget.budget_trackings && this.budget.budget_trackings.length > 0) {
        // Fallback to budget_trackings
        totalSpent = this.budget.budget_trackings.reduce(
          (total, tracking) => total + (tracking.actual_amount || 0), 0,
        );
      }

      return totalSpent;
    },

    totalRemaining() {
      return Math.max(0, this.totalBudget - this.totalSpent);
    },

    selectedCategoryImpact() {
      if (!this.form.category || !this.form.amount || !this.budget || !this.budget.categories) {
        return null;
      }

      const category = this.budget.categories[this.form.category];
      if (!category) return null;

      // Get current spent amount for this category
      let currentSpent = 0;
      if (this.budget.performance && this.budget.performance.categories
          && this.budget.performance.categories[this.form.category]) {
        currentSpent = this.budget.performance.categories[this.form.category].actual || 0;
      } else if (this.budget.recent_tracking) {
        currentSpent = this.budget.recent_tracking
          .filter((tracking) => tracking.category === this.form.category)
          .reduce((total, tracking) => total + (tracking.amount || 0), 0);
      } else if (this.budget.budget_trackings) {
        currentSpent = this.budget.budget_trackings
          .filter((tracking) => tracking.category === this.form.category)
          .reduce((total, tracking) => total + (tracking.actual_amount || 0), 0);
      }

      const allocated = category.amount || 0;
      const newAmount = parseFloat(this.form.amount) || 0;
      const newSpent = currentSpent + newAmount;
      const newProgress = allocated > 0 ? (newSpent / allocated) * 100 : 0;
      const overBudget = Math.max(0, newSpent - allocated);

      return {
        currentSpent,
        allocated,
        newSpent,
        newProgress: Math.min(newProgress, 150), // Cap at 150% for display
        overBudget,
      };
    },

    recentSpending() {
      return this.spendingEntries.slice(0, 5);
    },

    budgetName() {
      return (this.budget && this.budget.name) || 'Budget';
    },

    budgetCategories() {
      return (this.budget && this.budget.categories) || {};
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(newVal) {
        this.showModal = newVal;
        if (newVal && this.budget && this.budget.id) {
          this.fetchRecentSpending();
        }
      },
    },
    showModal(newVal) {
      this.$emit('update:visible', newVal);
    },
  },
  methods: {
    ...mapActions('budgets', ['trackSpending', 'fetchSpendingEntries']),

    formatCurrency,
    formatDate,

    getCategorySpent(categoryName) {
      if (!this.budget) return 0;

      // Get spent amount for specific category from performance data
      if (this.budget.performance && this.budget.performance.categories
          && this.budget.performance.categories[categoryName]) {
        return this.budget.performance.categories[categoryName].actual || 0;
      }

      // Try recent_tracking from new API format
      if (this.budget.recent_tracking) {
        return this.budget.recent_tracking
          .filter((tracking) => tracking.category === categoryName)
          .reduce((total, tracking) => total + (tracking.amount || 0), 0);
      }

      // Fallback to budget_trackings only if no other data available
      if (this.budget.budget_trackings) {
        return this.budget.budget_trackings
          .filter((tracking) => tracking.category === categoryName)
          .reduce((total, tracking) => total + (tracking.actual_amount || 0), 0);
      }

      return 0;
    },

    async fetchRecentSpending() {
      if (!this.budget || !this.budget.id) return;

      try {
        await this.fetchSpendingEntries({
          budgetId: this.budget.id,
          filters: { limit: 5 },
        });
      } catch (error) {
        console.error('Error fetching recent spending:', error);
      }
    },

    formatCategoryName(name) {
      return name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    },

    getImpactStatus() {
      if (!this.selectedCategoryImpact) return '';

      if (this.selectedCategoryImpact.newProgress > 100) {
        return 'Over Budget';
      } if (this.selectedCategoryImpact.newProgress > 80) {
        return 'Near Limit';
      }
      return 'On Track';
    },

    getImpactStatusClass() {
      if (!this.selectedCategoryImpact) return '';

      if (this.selectedCategoryImpact.newProgress > 100) {
        return 'status-danger';
      } if (this.selectedCategoryImpact.newProgress > 80) {
        return 'status-warning';
      }
      return 'status-success';
    },

    getProgressColor(progress) {
      if (progress > 100) return 'danger';
      if (progress > 80) return 'warning';
      return 'success';
    },

    handleInputError(error) {
      this.$set(this.errors, 'amount', error);
    },

    validateForm() {
      this.errors = {};

      if (!this.form.category) {
        this.$set(this.errors, 'category', 'Please select a category');
      }

      if (!this.form.amount) {
        this.$set(this.errors, 'amount', 'Amount is required');
      } else if (this.form.amount <= 0) {
        this.$set(this.errors, 'amount', 'Amount must be greater than 0');
      }

      if (!this.form.description.trim()) {
        this.$set(this.errors, 'description', 'Description is required');
      }

      return Object.keys(this.errors).length === 0;
    },

    async submitSpending() {
      if (!this.validateForm() || !this.budget || !this.budget.id) return;

      this.loading = true;

      try {
        const spendingData = {
          category: this.form.category,
          amount: parseFloat(this.form.amount),
          description: this.form.description.trim(),
          spending_date: this.form.spendingDate,
        };

        const result = await this.trackSpending({
          budgetId: this.budget.id,
          spendingData,
        });

        if (result.success) {
          this.$toast.success('Spending tracked successfully!');
          this.$emit('spending-tracked', result.data);
          this.closeModal();
        } else {
          this.$toast.error(result.error || 'Failed to track spending');
        }
      } catch (error) {
        console.error('Spending tracking error:', error);
        this.$toast.error('An error occurred while tracking spending');
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        category: '',
        amount: '',
        description: '',
        spendingDate: new Date().toISOString().split('T')[0],
      };
      this.errors = {};
    },

    closeModal() {
      this.resetForm();
      this.showModal = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/variable.scss';

.spending-tracker {
  .budget-summary {
    background: #F8F9FA;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1.5rem;

    h5 {
      margin-bottom: 1rem;
      color: $black;
    }
  }

  .summary-stats {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
  }

  .stat-item {
    text-align: center;
    flex: 1;

    .label {
      display: block;
      margin-bottom: 0.25rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .value {
      font-size: 16px;
      color: $black;
    }
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .form-label {
    display: block;
    margin-bottom: 0.5rem;
    color: $black;
  }

  .error-message {
    color: $primary;
    font-size: 12px;
    margin-top: 0.25rem;
  }

  .category-impact {
    background: $primary-light;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;

    h6 {
      margin-bottom: 0.75rem;
      color: $black;
    }
  }

  .impact-card {
    background: $white;
    border-radius: 6px;
    padding: 1rem;
  }

  .impact-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;

    h6 {
      margin-bottom: 0;
      color: $black;
      font-size: 14px;
    }
  }

  .impact-status {
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;

    &.status-success {
      background: rgba($success, 0.2);
      color: $success;
    }

    &.status-warning {
      background: rgba(#FF9800, 0.2);
      color: #FF9800;
    }

    &.status-danger {
      background: rgba($primary, 0.2);
      color: $primary;
    }
  }

  .progress-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    font-size: 12px;
  }

  .over-budget-warning {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding: 0.5rem;
    background: rgba($primary, 0.1);
    border-radius: 4px;
    font-size: 12px;
    color: $primary;

    i {
      color: $primary;
    }
  }

  .recent-spending {
    margin-bottom: 1.5rem;

    h6 {
      margin-bottom: 0.75rem;
      color: $black;
    }
  }

  .spending-list {
    max-height: 200px;
    overflow-y: auto;
  }

  .spending-entry {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem;
    background: #F8F9FA;
    border-radius: 6px;
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .entry-info {
    flex: 1;
    min-width: 0;

    .entry-description {
      display: block;
      font-size: 14px;
      color: $black;
      font-weight: 500;
      margin-bottom: 0.25rem;
    }

    .entry-category {
      text-transform: capitalize;
    }
  }

  .entry-details {
    text-align: right;
    flex-shrink: 0;

    .entry-amount {
      display: block;
      font-size: 14px;
      margin-bottom: 0.25rem;
    }
  }

  .modal-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
    padding-top: 1rem;
    border-top: 1px solid #E0E0E0;

    .btn {
      min-width: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }
  }
}

@media (max-width: 576px) {
  .spending-tracker {
    .summary-stats {
      flex-direction: column;
      gap: 0.75rem;
    }

    .stat-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      text-align: left;

      .label {
        margin-bottom: 0;
      }
    }

    .impact-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
    }

    .spending-entry {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
    }

    .entry-details {
      text-align: left;
      width: 100%;
      display: flex;
      justify-content: space-between;
    }

    .modal-actions {
      flex-direction: column;

      .btn {
        width: 100%;
      }
    }
  }
}
</style>
